import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { downloadApiFile } from '../../api/download';
import { getReceivedDocument, getSentDocument } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import { CopyableText } from '../../components/CopyableText';
import { ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { GateStatusChip } from '../../components/StatusChips';
import { useErrorToast } from '../../hooks/useErrorToast';
import { useSelectedTenant } from '../../tenant/tenantSelection';
import { formatAmount, formatDate, formatDateTime } from '../../utils/format';
import type { FlowisReceivedDocument, FlowisSentDocument } from '../../api/types';

export type FlowisDirection = 'sent' | 'received';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ minWidth: 180 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {typeof children === 'string' ? <Typography variant="body2">{children}</Typography> : children}
    </Box>
  );
}

/** A gate-issued link is dead once its expiry has passed; no expiry means it does not expire. */
function hasExpired(expiresAt: string | null | undefined): boolean {
  return expiresAt !== undefined && expiresAt !== null && new Date(expiresAt).getTime() < Date.now();
}

/** The gate leaves the tax id out for some parties; the name then stands alone rather than "(null)". */
function partyLabel(name: string | null, taxId: string | null): string {
  const base = name ?? '—';
  return taxId ? `${base} (${taxId})` : base;
}

export interface FlowisDocumentDialogProps {
  accountGid: string;
  direction: FlowisDirection;
  documentId: string | null;
  onClose: () => void;
}

export function FlowisDocumentDialog({
  accountGid,
  direction,
  documentId,
  onClose,
}: FlowisDocumentDialogProps) {
  const toastError = useErrorToast();
  const { t } = useTranslation();
  const { selectedTenantGid } = useSelectedTenant();

  const query = useQuery<FlowisSentDocument | FlowisReceivedDocument>({
    queryKey:
      direction === 'sent'
        ? queryKeys.flowis.sentDetail(selectedTenantGid, accountGid, documentId ?? '')
        : queryKeys.flowis.receivedDetail(selectedTenantGid, accountGid, documentId ?? ''),
    queryFn: ({ signal }) =>
      direction === 'sent'
        ? getSentDocument(accountGid, documentId ?? '', signal)
        : getReceivedDocument(accountGid, documentId ?? '', signal),
    enabled: documentId !== null && accountGid.length > 0,
  });

  const document = query.data;

  // Only the sent detail carries the source document the invoice was built from, and `direction` is
  // what chose the endpoint the response came from.
  const sent = document && direction === 'sent' ? (document as FlowisSentDocument) : null;

  const downloadPdf = async () => {
    if (!documentId) return;
    try {
      await downloadApiFile(`/flowis/accounts/${accountGid}/${direction}/${documentId}/pdf`, {
        fallbackFileName: `${document?.invoiceId ?? documentId}.pdf`,
      });
    } catch (error) {
      toastError(error);
    }
  };

  const xmlExpired = hasExpired(document?.xmlFileUrlExpiresAt);
  const sourceExpired = hasExpired(sent?.sourceDocumentUrlExpiresAt);

  return (
    <Dialog open={documentId !== null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {direction === 'sent' ? t('flowis.document.sentTitle') : t('flowis.document.receivedTitle')}
        {document?.invoiceId ? ` — ${document.invoiceId}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {query.isPending && <LoadingBlock />}
        {query.isError && <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />}

        {document && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <GateStatusChip status={document.status} />
              {document.downloaded && (
                <Typography variant="body2" color="text.secondary">
                  {t('flowis.document.markedConsumed')}
                </Typography>
              )}
            </Stack>

            {document.errorCode && (
              <Alert severity="error">
                <Trans
                  i18nKey="flowis.document.errorBehind"
                  values={{ errorCode: document.errorCode }}
                  components={{ code: <code /> }}
                />
              </Alert>
            )}

            <Stack direction="row" spacing={4} useFlexGap flexWrap="wrap">
              <Field label={t('flowis.document.documentId')}>
                {document.id ? <CopyableText value={document.id} /> : '—'}
              </Field>
              <Field label={t('flowis.document.invoiceNumber')}>{document.invoiceId ?? '—'}</Field>
              <Field label={t('flowis.document.type')}>{document.documentType ?? '—'}</Field>
              <Field label={t('flowis.document.issued')}>{formatDate(document.issueDate)}</Field>
              <Field label={t('flowis.document.due')}>{formatDate(document.dueDate)}</Field>
              <Field label={t('flowis.document.taxableSupply')}>{formatDate(document.taxationDate)}</Field>
              <Field label={t('flowis.document.createdInFlowis')}>{formatDateTime(document.createdAt)}</Field>
              {document.isSelfBilling !== null && (
                <Field label={t('flowis.document.selfBilling')}>
                  {document.isSelfBilling ? t('states.yes') : t('states.no')}
                </Field>
              )}
            </Stack>

            <Divider />

            <Stack direction="row" spacing={4} useFlexGap flexWrap="wrap">
              <Field label={t('flowis.document.seller')}>
                {partyLabel(document.sellerRegistrationName, document.sellerCompanyTaxId)}
              </Field>
              <Field label={t('flowis.document.buyer')}>
                {partyLabel(document.buyerRegistrationName, document.buyerCompanyTaxId)}
              </Field>
            </Stack>

            <Stack direction="row" spacing={4} useFlexGap flexWrap="wrap">
              <Field label={t('flowis.document.taxExclusive')}>
                {formatAmount(document.totalTaxExclusiveAmount, document.documentCurrencyCode)}
              </Field>
              <Field label={t('flowis.document.tax')}>
                {formatAmount(document.totalTaxAmount, document.documentCurrencyCode)}
              </Field>
              <Field label={t('flowis.document.totalInclVat')}>
                {formatAmount(document.totalVatIncluded, document.documentCurrencyCode)}
              </Field>
            </Stack>

            <Divider />

            <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
              <Button startIcon={<DownloadIcon />} variant="outlined" onClick={() => void downloadPdf()}>
                {t('flowis.document.downloadPdf')}
              </Button>

              {/*
                The only link in this app that is a plain anchor: the XML URL is a time-limited
                storage URL carrying its own authorization, so it must NOT get our bearer token.
              */}
              {document.xmlFileUrl && (
                <Button
                  startIcon={<OpenInNewIcon />}
                  component="a"
                  href={document.xmlFileUrl}
                  target="_blank"
                  rel="noopener"
                  disabled={xmlExpired}
                >
                  {t('flowis.document.openXml')}
                </Button>
              )}

              {document.xmlFileUrlExpiresAt && (
                <Typography variant="caption" color={xmlExpired ? 'error' : 'text.secondary'}>
                  {xmlExpired
                    ? t('flowis.document.xmlExpired', {
                        time: formatDateTime(document.xmlFileUrlExpiresAt),
                      })
                    : t('flowis.document.xmlValidUntil', {
                        time: formatDateTime(document.xmlFileUrlExpiresAt),
                      })}
                </Typography>
              )}
            </Stack>

            {sent?.sourceDocumentUrl && (
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  startIcon={<OpenInNewIcon />}
                  component="a"
                  href={sent.sourceDocumentUrl}
                  target="_blank"
                  rel="noopener"
                  disabled={sourceExpired}
                >
                  {t('flowis.document.openSource')}
                </Button>
                {sent.sourceDocumentUrlExpiresAt && (
                  <Typography variant="caption" color={sourceExpired ? 'error' : 'text.secondary'}>
                    {sourceExpired
                      ? t('flowis.document.sourceExpired', {
                          time: formatDateTime(sent.sourceDocumentUrlExpiresAt),
                        })
                      : t('flowis.document.sourceValidUntil', {
                          time: formatDateTime(sent.sourceDocumentUrlExpiresAt),
                        })}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
